'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav_home', label: 'HOME', href: '/homepage' },
    { id: 'nav_services', label: 'SERVICES', href: '/services' },
    { id: 'nav_portfolio', label: 'PORTFOLIO', href: '/portfolio' },
  ];

  const isActive = (href: string) => pathname === href;

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/80 backdrop-blur-sm border-b border-border shadow-md'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-custom h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 group">
          <div className="relative h-10 w-auto group-hover:scale-105 transition-transform">
            <Image
              src="/assets/images/M_MARKET_horizont_-1770066595193.png"
              alt="Murov Marketing Logo"
              width={150}
              height={40}
              className="object-contain"
              priority
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8 text-xs font-bold tracking-widest">
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              className={`relative hover:text-primary transition-colors ${
                isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {link.label}
              {isActive(link.href) && (
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-accent"></span>
              )}
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <div className="hidden md:block">
          <a
            href="https://wa.me/994514093900"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm uppercase tracking-wider"
          >
            Get in Touch
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <Icon
            name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
            size={24}
            className="text-foreground"
          />
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background border-t border-border shadow-lg">
          <div className="container-custom py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`block text-sm font-bold tracking-widest hover:text-primary transition-colors ${
                  isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="https://wa.me/994514093900"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm uppercase tracking-wider w-full text-center block"
              onClick={() => setIsMenuOpen(false)}
            >
              Get in Touch
            </a>
          </div>
        </div>
      )}
    </header>
  );
}